/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../../lib/amplify-client';

/**
 * Gift feature TanStack Query hooks (Phase D — PR D3, Task 11).
 *
 * `useGetGift`  — public query (apiKey) for the gift landing page.
 * `useClaimGiftMutation` — authenticated mutation (userPool) to claim a gift.
 */

export interface GiftPublicView {
  gift_id: string;
  sender_name: string;
  note?: string | null;
  pack_name: string;
  quantity: number;
  pack_art_url?: string | null;
  status: string;
}

export function useGetGift(giftId: string | undefined) {
  return useQuery({
    queryKey: ['gift', giftId],
    queryFn: async () => {
      const result = await (client.queries as any).getGift(
        { gift_id: giftId },
        { authMode: 'apiKey' },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return result.data as GiftPublicView | null;
    },
    enabled: !!giftId,
  });
}

export function useClaimGiftMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (giftId: string) => {
      const result = await (client.mutations as any).claimGift(
        { gift_id: giftId },
        { authMode: 'userPool' },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return result.data as { gift_id: string; pack_id: string; quantity: number };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pack-inventory'] });
    },
  });
}

export function useClaimPendingGifts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await (client.mutations as any).claimPendingGifts(
        {},
        { authMode: 'userPool' },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return result.data as { claimed: number; failed: number };
    },
    onSuccess: (data) => {
      if (data && data.claimed > 0) {
        void queryClient.invalidateQueries({ queryKey: ['pack-inventory'] });
      }
    },
  });
}
